import { EUROPACE_INSURED_RISKS, LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import { z } from 'zod';

import { schemaId } from '../../utils/schema.utils';
import { schemaConsorsGetSubscription } from '../Consors';
import { schemaEuropaceAntrag } from '../Europace';
import { schemaEuropaceCommission } from '../Europace/Angebote/Commission';

const schemaResidualDebtInsurance = z.array(z.nativeEnum(EUROPACE_INSURED_RISKS));
export type SchemaResidualDebtInsurance = z.infer<typeof schemaResidualDebtInsurance>;

const schemaAcceptedOfferCommon = z.object({
    id: schemaId,
});
const schemaAcceptedOfferEuropace = z
    .object({
        commission: schemaEuropaceCommission.optional(),
        gesamtkreditbetrag: schemaEuropaceAntrag.shape.gesamtkonditionen._def.innerType.shape.gesamtkreditbetrag,
        laufzeitInMonaten: schemaEuropaceAntrag.shape.gesamtkonditionen._def.innerType.shape.laufzeitInMonaten,
        nettokreditbetrag: schemaEuropaceAntrag.shape.gesamtkonditionen._def.innerType.shape.nettokreditbetrag,
        offerRequestId: schemaId,
        produktanbieterantragsnummer: schemaEuropaceAntrag.shape.produktanbieterantragsnummer,
        rateMonatlich: schemaEuropaceAntrag.shape.gesamtkonditionen._def.innerType.shape.rateMonatlich,
        sollzins: schemaEuropaceAntrag.shape.gesamtkonditionen._def.innerType.shape.sollzins,
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.EUROPACE),
    })
    .merge(schemaAcceptedOfferCommon);

const schemaAcceptedOfferEuropaceSync = schemaAcceptedOfferEuropace.omit({ offerRequestId: true, source: true }).merge(
    z.object({
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.EUROPACE_SYNC),
    }),
);

const schemaAcceptedOfferManual = z
    .object({
        gesamtkreditbetrag: z.number().nullable(),
        laufzeitInMonaten: z.number().nullable(),
        nettokreditbetrag: z.number().nullable(),
        rateMonatlich: z.number().nullable(),
        sollzins: z.number().nullable(),
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.MANUAL),
    })
    .merge(schemaAcceptedOfferCommon);

const schemaAcceptedOfferConsors = z
    .object({
        gesamtkreditbetrag: schemaConsorsGetSubscription.shape.subscriptionList
            .unwrap()
            .element.shape.financialFramework.unwrap().shape.creditAmount,
        laufzeitInMonaten: schemaConsorsGetSubscription.shape.subscriptionList
            .unwrap()
            .element.shape.financialFramework.unwrap().shape.duration,
        nettokreditbetrag: schemaConsorsGetSubscription.shape.subscriptionList
            .unwrap()
            .element.shape.financialFramework.unwrap().shape.loanAmount,
        offerRequestId: schemaId,
        providerApplicationNumber: z.string().optional(),
        rateMonatlich: schemaConsorsGetSubscription.shape.subscriptionList
            .unwrap()
            .element.shape.financialFramework.unwrap().shape.nominalRate,
        sollzins: schemaConsorsGetSubscription.shape.subscriptionList.unwrap().element.shape.financialFramework.unwrap()
            .shape.effectiveRate,
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.CONSORS),
    })
    .merge(schemaAcceptedOfferCommon);

export type SchemaAcceptedOfferConsors = z.infer<typeof schemaAcceptedOfferConsors>;

export const schemaLoanRequestAcceptedOffers = z.array(
    z.discriminatedUnion('source', [
        schemaAcceptedOfferEuropace,
        schemaAcceptedOfferManual,
        schemaAcceptedOfferConsors,
        schemaAcceptedOfferEuropaceSync,
    ]),
);

export type SchemaLoanRequestAcceptedOffers = z.infer<typeof schemaLoanRequestAcceptedOffers>;
